/**
 * @file components/non-conformities/use-findings.ts
 * @description 「問題與報告 > 問題追蹤」三個 Tab（NC 報告 / 觀察事項 / 追蹤稽核報告）
 *   共用的資料型別與 TanStack Query hook。
 *
 * 資料源：`GET /api/nc-reports`（後端聚合 audit_findings —— 現場實地稽核已填的發現事項）。
 *   後端已做行級權限過濾（稽核員只看「自己被指派客戶」的發現，含往年案件歷史），前端不重複判斷。
 *   透過 `type` 參數區分三種視圖：
 *     - nonconformity → NC 報告（不符合事項，severity 重大 / 一般）
 *     - observation   → 觀察事項（severity 觀察事項 / 改善建議）
 *     - followup      → 追蹤稽核報告（需改正的發現的改善追蹤生命週期）
 *
 * 分頁遵守 CLAUDE.md §5：顯式傳 page / pageSize，queryKey 帶上，Table 受控 total。
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/** 對外（前端）嚴重程度三值。 */
export type FindingSeverity = 'minor' | 'major' | 'critical';

/** 由 closedAt / clientResponseAt 推導的處理狀態。 */
export type FindingStatus = 'open' | 'correcting' | 'closed';

/** 視圖類型（對應三 Tab）。 */
export type FindingType = 'nonconformity' | 'observation' | 'followup';

/**
 * 稽核發現事項 DTO（對應 `GET /api/nc-reports` 回傳 item）。
 * 三個 Tab 共用同一形狀，各自取用需要的欄位。
 */
export interface FindingItem {
  /** audit_findings.id */
  id: number;
  /** 發現編號（NC-...） */
  ncNumber: string;
  /** 案件內部 ID（跳詳情用） */
  caseId: number;
  /** 案件編號（顯示用，如 2026-TAP0001-01） */
  caseCode: string | null;
  auditTaskId: number | null;
  /** 驗證類型（初次申請 / 追蹤查驗1 / …） */
  verificationType: string | null;
  clientId: number;
  clientName: string | null;
  /** 驗證註冊碼（clients.clientCode，如 ZH-CAS-0001） */
  registerCode: string | null;
  severity: FindingSeverity;
  /** 相關條款 */
  clause: string | null;
  /** 發現事項描述 */
  description: string;
  evidence: string | null;
  /** 是否需要改正（1=需要，0=不需要） */
  correctionRequired: number;
  /** 改正期限（ISO） */
  deadline: string | null;
  /** 客戶回覆 / 改善結果描述 */
  clientResponse: string | null;
  /** 客戶回覆時間（ISO） */
  clientResponseAt: string | null;
  status: FindingStatus;
  auditorId: number | null;
  /** 稽核員（開立者）姓名 */
  auditorName: string | null;
  /** 改善關閉審核人姓名 */
  closedByName: string | null;
  /** 建立日期（ISO） */
  createdDate: string | null;
  /** 關閉日期（ISO） */
  closedDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 分頁回應。 */
export interface FindingListResponse {
  items: FindingItem[];
  total: number;
  page: number;
  pageSize: number;
}

/** hook 入參。 */
interface UseFindingsParams {
  /** 視圖類型 */
  type: FindingType;
  /** 頁碼（1-based） */
  page: number;
  /** 每頁筆數 */
  pageSize: number;
  /** 關鍵字（後端跨欄位 ilike，涵蓋全量而非僅當前頁） */
  keyword?: string;
  /** 處理狀態過濾（後端由 closedAt / clientResponseAt 反推） */
  status?: FindingStatus;
  /** 嚴重程度過濾（後端映射到中文 severity 集合） */
  severity?: FindingSeverity;
}

/**
 * 查詢稽核發現事項（服務器端分頁 + 服務器端搜尋 / 狀態過濾）。
 *
 * 搜尋（keyword）與狀態（status）皆下推後端過濾，total 反映過濾後筆數，
 * 因此分頁計數與可見資料一致、搜尋涵蓋全量（非僅當前頁）。
 */
export function useFindings({
  type,
  page,
  pageSize,
  keyword,
  status,
  severity,
}: UseFindingsParams) {
  return useQuery<FindingListResponse>({
    // queryKey 帶上所有影響結果的條件，確保翻頁 / 搜尋 / 過濾 / 切 Tab 都重新請求
    queryKey: ['nc-reports', type, page, pageSize, keyword ?? '', status ?? '', severity ?? ''],
    queryFn: () => {
      const params = new URLSearchParams({
        type,
        page: String(page),
        pageSize: String(pageSize),
      });
      if (keyword) params.set('keyword', keyword);
      if (status) params.set('status', status);
      if (severity) params.set('severity', severity);
      return apiClient.get<FindingListResponse>(`/api/nc-reports?${params.toString()}`);
    },
  });
}

/**
 * 值防抖 hook：延遲回傳最新值，避免搜尋框每次按鍵都觸發請求。
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * ISO 時間字串 → 本地日期 YYYY-MM-DD（null 安全）。
 *
 * ⚠️ 必須用本地時區格式化：`new Date(iso).toISOString()` 會回 UTC 日期，
 * 台灣（UTC+8）傍晚建立的記錄會顯示成前一天（off-by-one）。
 */
export function formatLocalDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
